import { useEffect, useRef, useState } from 'react';
import { notify } from '@/lib/notify';
import { isStepConflict } from '@/lib/stepConflict';
import { Button } from '@ai-tutor/ui';
import {
  FileText,
  ExternalLink,
  Video,
  FileIcon,
  FileSpreadsheet,
  Presentation,
  Image as ImageIcon,
  MoreVertical,
  Trash2,
  Eye,
  Download,
  Loader2,
  AlertCircle,
  AlertTriangle,
  PauseCircle,
  Pencil,
  Check,
  RefreshCw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Source } from '@/data/fixtures';
import type {
  SourceItem as ApiSourceItem,
  SourceStatus,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useDismiss } from '@/hooks/ui/useDismiss';
import { parseUtcDate } from '@/lib/datetime';
import { useRenameSource, useRetrySource } from '@/hooks/lesson/useSources';

type AnySource = Source | ApiSourceItem;

interface SourceItemProps {
  source: AnySource;
  onRemove: () => void;
  onView?: (source: AnySource) => void;
  /**
   * Staged-selection props. When `staged` is undefined the checkbox is
   * hidden (e.g. mock mode or contexts without a staged set). When defined
   * the checkbox renders, reflecting `staged`; `onToggleStaged` fires with
   * the new desired checked state. `stagingDisabled` greys it out while a
   * regenerate / mutation is in flight.
   */
  staged?: boolean;
  onToggleStaged?: (checked: boolean) => void;
  stagingDisabled?: boolean;
  /**
   * Read-only mode - used when viewing a non-latest version. Renders the
   * check state (so the user can see which sources the past version used)
   * but disables toggling and suppresses hover affordances.
   */
  readOnly?: boolean;
  /**
   * Whether a failed source may be retried inline. Suppressed in the
   * total-failure state (every source failed) - there the single
   * "Retry all sources" action in the center panel is the right tool, and
   * per-row retries would let the lesson silently build on just one source.
   * Per-row retry still shows for partial failures (one bad file among good).
   */
  canRetry?: boolean;
}

/** Check if source is from the API (has source_id field). */
function isApiSource(source: AnySource): source is ApiSourceItem {
  return 'source_id' in source;
}

/** Status pill - surfaces processing / indexing / failed states explicitly
 *  so the user can tell at a glance whether a source is usable. Ready
 *  sources render no pill (less noise on the happy path).
 */
function StatusPill({
  status,
  blocked = false,
}: {
  status: SourceStatus;
  blocked?: boolean;
}) {
  if (status === 'ready') return null;
  // A content-policy block reuses the `failed` status but reads differently:
  // amber (not rose) and "Blocked", since it's a policy outcome, not an error.
  if (blocked) {
    return (
      <span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'>
        <AlertTriangle className='h-3 w-3' />
        Blocked
      </span>
    );
  }
  const config: Record<
    Exclude<SourceStatus, 'ready'>,
    { label: string; className: string; icon: React.ReactNode }
  > = {
    pending: {
      label: 'Queued',
      className: 'bg-muted text-muted-foreground',
      icon: <Loader2 className='h-3 w-3 animate-spin' />,
    },
    processing: {
      // Indigo matches the panel's accent (count badge, version selector,
      // spinner icon), so the "in progress" signal reads as part of the
      // same theme rather than a stray blue chip.
      label: 'Processing',
      className: 'bg-brand-100 text-brand-700 dark:tint-brand',
      icon: <Loader2 className='h-3 w-3 animate-spin' />,
    },
    failed: {
      // Muted rose - same hue as the failed-state file icon.
      label: 'Failed',
      className:
        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
      icon: <AlertCircle className='h-3 w-3' />,
    },
    awaiting_scope: {
      // Large PDF paused until the user picks chapters. The action lives in the
      // center panel's checkpoint, so this is a quiet, non-actionable status —
      // no bright CTA competing with it here.
      label: 'Paused',
      className: 'bg-muted text-muted-foreground',
      icon: <PauseCircle className='h-3 w-3' />,
    },
  };
  const c = config[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        c.className
      )}
    >
      {c.icon}
      {c.label}
    </span>
  );
}

export function SourceItem({
  source,
  onRemove,
  onView,
  staged,
  onToggleStaged,
  stagingDisabled,
  readOnly = false,
  canRetry = true,
}: SourceItemProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useDismiss(menuRef, showMenu, () => setShowMenu(false));
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(source.name);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const sourceType = isApiSource(source) ? source.type : source.type;
  const sourceName = source.name;
  const apiStatus: SourceStatus | null = isApiSource(source)
    ? source.status
    : null;
  // Rename only applies to API-mode sources - mock sources are local
  // state with no backing endpoint. We grab the parent lesson_id from
  // the source itself so SourceItem stays prop-light.
  const renameLessonId = isApiSource(source) ? source.lesson_id : null;
  const renameMutation = useRenameSource(renameLessonId || '');
  const retryMutation = useRetrySource(renameLessonId || '');

  const handleRetrySource = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isApiSource(source) || !renameLessonId) return;
    retryMutation.mutate(source.source_id, {
      onSuccess: () => notify.success('Retrying source…'),
      onError: err => {
        // 409 is handled calmly by the hook (refresh + neutral toast).
        if (isStepConflict(err)) return;
        notify.error(
          (err as { message?: string })?.message || "Couldn't retry source"
        );
      },
    });
  };

  // Focus + select the filename when entering rename mode so users can
  // immediately type a replacement.
  useEffect(() => {
    if (isRenaming) {
      requestAnimationFrame(() => {
        renameInputRef.current?.focus();
        renameInputRef.current?.select();
      });
    }
  }, [isRenaming]);
  const isProcessing = apiStatus === 'pending' || apiStatus === 'processing';
  const isFailed = apiStatus === 'failed';
  // A content-moderation block reuses `failed` status but is distinguished by
  // its error code. It reads as amber "Blocked", hides Retry (a retry would
  // just re-block), and shows the policy message inline.
  const sourceError = isApiSource(source)
    ? (source.error as { code?: string; message?: string } | null)
    : null;
  const isBlocked = isFailed && sourceError?.code === 'SOURCE_CONTENT_BLOCKED';
  // Set when the document exceeded the plan's per-source page cap (only on
  // API sources - the union's other member has no such field).
  const pageLimitApplied = isApiSource(source)
    ? (source.page_limit_applied ?? null)
    : null;
  const pageCount = isApiSource(source) ? (source.page_count ?? null) : null;
  // Soft-deleted sources are returned by the API so past-version views can
  // surface them; they're inert (no actions, muted styling, "Deleted" badge).
  const isSoftDeleted = isApiSource(source) && source.is_active === false;
  const isReady =
    (apiStatus === 'ready' || apiStatus === null) && !isSoftDeleted;

  const getSourceIcon = () => {
    // Spinner / alert overrides take precedence so the row's leading icon
    // tells you at a glance what state the source is in. Colors are muted
    // (-500/70 opacity) so the file row feels editorial rather than
    // "Office 365"-loud, while still differentiating by type.
    if (isProcessing) {
      return (
        <Loader2 className='h-4 w-4 animate-spin text-brand-500/80 dark:text-primary' />
      );
    }
    if (isBlocked) {
      return <AlertTriangle className='h-4 w-4 text-amber-500/80' />;
    }
    if (isFailed) {
      return <AlertCircle className='h-4 w-4 text-rose-500/80' />;
    }

    if (sourceType === 'link') {
      return <ExternalLink className='h-4 w-4 text-slate-500/80' />;
    }
    if (sourceType === 'video') {
      return <Video className='h-4 w-4 text-violet-500/80' />;
    }

    // File sources - diversify per extension. Muted color buckets:
    //   • PDF                 → rose       (distinctive document red)
    //   • Word / text docs    → slate      (neutral document gray-blue)
    //   • Slides              → amber      (warm "presentation" hue)
    //   • Spreadsheets / CSV  → emerald    (the universal "sheet" green)
    //   • Images              → violet     (shares family with video)
    //   • Everything else     → gray fallback
    const ext = getDisplayType().toLowerCase();

    const WORD_DOCS = new Set(['doc', 'docx', 'dotx', 'rtf', 'txt', 'wpd']);
    const SLIDES = new Set(['ppt', 'pptx']);
    const SHEETS = new Set([
      'xls',
      'xlsx',
      'xlsm',
      'xltx',
      'xltm',
      'csv',
      'qpw',
    ]);
    const IMAGES = new Set([
      'image',
      'png',
      'jpg',
      'jpeg',
      'gif',
      'bmp',
      'tif',
      'tiff',
      'heic',
      'heif',
      'psd',
      'pcx',
      'ppm',
      'apng',
      'cur',
      'dcx',
      'ftex',
      'pixar',
    ]);

    if (ext === 'pdf') {
      return <FileText className='h-4 w-4 text-rose-500/80' />;
    }
    if (WORD_DOCS.has(ext)) {
      return <FileText className='h-4 w-4 text-slate-500/80' />;
    }
    if (SLIDES.has(ext)) {
      return <Presentation className='h-4 w-4 text-amber-500/80' />;
    }
    if (SHEETS.has(ext)) {
      return <FileSpreadsheet className='h-4 w-4 text-emerald-500/80' />;
    }
    if (IMAGES.has(ext)) {
      return <ImageIcon className='h-4 w-4 text-violet-500/80' />;
    }

    return <FileIcon className='h-4 w-4 text-gray-500/80' />;
  };

  const getDisplayType = (): string => {
    // Prefer the actual file extension from the original filename - that's
    // the most specific signal we have ("docx", "xlsx", "md", "html", …)
    // and matches what the backend's text-extraction pipeline supports.
    if (isApiSource(source)) {
      const ext = source.original_filename?.split('.').pop()?.toLowerCase();
      if (
        ext &&
        ext.length >= 1 &&
        ext.length <= 6 &&
        /^[a-z0-9]+$/.test(ext)
      ) {
        return ext;
      }
      // Fallback: mime-type category for files without an obvious extension.
      if (source.mime_type?.startsWith('application/pdf')) return 'pdf';
      if (source.mime_type?.startsWith('image/')) return 'image';
      if (source.mime_type?.startsWith('video/')) return 'video';
      if (source.mime_type?.startsWith('audio/')) return 'audio';
      return source.type;
    }
    return source.type;
  };

  const getSize = (): string | null => {
    if (isApiSource(source) && source.file_size) {
      const mb = source.file_size / (1024 * 1024);
      return mb >= 1
        ? `${mb.toFixed(1)} MB`
        : `${(source.file_size / 1024).toFixed(0)} KB`;
    }
    if (!isApiSource(source) && source.size) {
      return source.size;
    }
    return null;
  };

  const getTimeAgo = (): string => {
    if (isApiSource(source)) {
      // parseUtcDate handles the backend's naive-UTC strings - without it
      // a row created right now reads as "6 hours ago" for IST users.
      return formatDistanceToNow(parseUtcDate(source.created_at), {
        addSuffix: true,
      });
    }
    return formatDistanceToNow(source.uploadedAt, { addSuffix: true });
  };

  // `stopPropagation` is load-bearing, not defensive. The menu is rendered
  // INSIDE the row, whose own `onClick` is also `onView` — so without this a
  // kebab View bubbled to the row and logged the read TWICE, silently
  // double-counting every kebab-opened source in the engagement strip. Rename
  // and Remove in the same menu already stop propagation; View was the one
  // that did not. Caught by
  // `App/MyLearning/Desktop/Behaviour/Lesson/Sources/OpensASource`.
  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.(source);
    setShowMenu(false);
  };

  const size = getSize();
  const showDownload = isApiSource(source)
    ? source.type === 'file'
    : source.type === 'pdf';

  // Disable click-to-view when the source isn't usable - mid-ingest (broken
  // iframe / missing chunks) or soft-deleted (file is gone, viewing would
  // just 404).
  const handleRowClick = () => {
    if (isRenaming) return;
    if (isProcessing || isFailed || isSoftDeleted) return;
    onView?.(source);
  };

  const beginRename = () => {
    setDraftName(source.name);
    setIsRenaming(true);
    setShowMenu(false);
  };

  const cancelRename = () => {
    setIsRenaming(false);
    setDraftName(source.name);
  };

  const commitRename = async () => {
    if (!isApiSource(source) || !renameLessonId) {
      cancelRename();
      return;
    }
    const next = draftName.trim();
    if (!next || next === source.name) {
      cancelRename();
      return;
    }
    try {
      await renameMutation.mutateAsync({
        sourceId: source.source_id,
        name: next,
      });
      notify.success('Source renamed');
    } catch (err: any) {
      notify.error(err, "Couldn't rename source");
    } finally {
      setIsRenaming(false);
    }
  };

  // Left-accent border is always 2px wide so the row doesn't reflow on toggle.
  // Color matches the rest of the border when unstaged; flips to the
  // foreground tone when staged (Linear-style row selection indicator).
  const isStaged = staged === true;
  const checkDisabled = stagingDisabled || isProcessing || isFailed;

  return (
    <div
      className={cn(
        'group flex items-center justify-between rounded-lg border border-l-2 p-3.5 shadow-sm transition-all duration-200',
        isStaged
          ? 'border-border/60 border-l-gray-900 bg-gray-900/[0.025] dark:border-l-primary dark:bg-accent'
          : 'border-border/60 border-l-gray-200/60 bg-gray-50/80 dark:border-l-gray-700/60 dark:bg-white/[0.02]',
        isReady && 'cursor-pointer',
        // Hover cue only on rows that aren't already selected/staged - a
        // selected row shouldn't repaint on hover (mirrors FlowSelector).
        isReady &&
          !isStaged &&
          'hover:border-gray-300 hover:bg-gray-900/[0.025] hover:shadow-md dark:hover:border-border dark:hover:bg-accent',
        // `opacity-80` was here and is gone. It dragged EVERY text colour in
        // the row below AA at once: the metadata line from 6.46:1 to 4.26:1
        // and the "Queued" pill to 3.41:1 at 10.4px. The row already says it
        // is processing three other ways — a spinner icon, a status pill, and
        // a hidden kebab — so the fade added nothing but the failure. Same
        // class of bug as the four opacity-on-small-text fixes in Phase 3.
        isProcessing && 'cursor-default',
        isFailed && 'cursor-default border-red-200/70 dark:border-red-900/40',
        // `opacity-60` was here too, and took the metadata line to 2.76:1 —
        // worse than the processing row it mirrors. The "Deleted" badge and
        // the absent actions already say the row is inert, explicitly, which
        // a blanket fade never did for anyone using a screen reader anyway.
        isSoftDeleted && 'cursor-default'
      )}
      onClick={handleRowClick}
    >
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        {staged !== undefined && (
          <button
            type='button'
            role='checkbox'
            aria-checked={isStaged}
            aria-disabled={readOnly || undefined}
            aria-label={
              readOnly
                ? `${sourceName} ${isStaged ? 'used' : 'not used'} in this version`
                : `Include ${sourceName} in next version`
            }
            disabled={checkDisabled || readOnly}
            onClick={e => {
              e.stopPropagation();
              if (readOnly) return;
              onToggleStaged?.(!isStaged);
            }}
            title={
              readOnly
                ? isStaged
                  ? 'Used in this version'
                  : 'Not used in this version'
                : isProcessing
                  ? 'Still processing'
                  : isFailed
                    ? 'Processing failed'
                    : isStaged
                      ? 'Included in next version'
                      : 'Not included - click to add'
            }
            className={cn(
              // 18px drawn, 44px tapped on a phone or touch screen.
              'tap-target group/check flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border transition-all',
              isStaged
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-gray-300 bg-card dark:border-gray-600',
              // Hover affordance only when interactive - read-only checks
              // shouldn't react to hover at all.
              !readOnly &&
                !isStaged &&
                'hover:border-gray-500 dark:hover:border-gray-400',
              // Disabled-but-readable for read-only mode: keep full opacity
              // so the user can scan which sources were used. Fall back to
              // the standard greyed-out look for genuine disabled states
              // (processing / failed / mutation in flight).
              readOnly
                ? 'cursor-default'
                : 'disabled:cursor-not-allowed disabled:opacity-40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 dark:focus-visible:ring-ring'
            )}
          >
            {isStaged ? (
              <Check className='h-3 w-3' strokeWidth={3} />
            ) : (
              <Check
                className={cn(
                  'h-3 w-3 text-muted-foreground transition-opacity',
                  // Hover ghost-check only in interactive mode.
                  readOnly
                    ? 'opacity-0'
                    : 'opacity-0 group-hover/check:opacity-60'
                )}
                strokeWidth={3}
              />
            )}
          </button>
        )}
        {getSourceIcon()}
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            {isRenaming ? (
              <input
                ref={renameInputRef}
                // The rename field has no visible label — it replaces the
                // filename in place — so without this it had no accessible
                // name at all (axe `label`). It surfaced intermittently
                // because axe only sees it while rename mode is open.
                aria-label='Rename source'
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    // Cancels the rename only - not the sources sheet this
                    // row may sit in, which closes on Escape too.
                    e.stopPropagation();
                    cancelRename();
                  }
                }}
                onBlur={commitRename}
                disabled={renameMutation.isPending}
                maxLength={200}
                // `globals.css` applies `ring-2 ring-ring ring-offset-2` to
                // every focus-visible input via a base-layer rule -
                // matches the EditLessonDetailsModal override so there's
                // no blue halo / outline when the user is typing.
                className={cn(
                  'flex-1 truncate rounded-sm border border-border bg-background px-1.5 py-0.5 text-sm font-semibold leading-tight text-foreground',
                  '!outline-none focus:!outline-none focus:!ring-0 focus:!ring-offset-0',
                  'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0'
                )}
              />
            ) : (
              <p className='truncate text-sm font-semibold leading-tight text-foreground'>
                {sourceName}
              </p>
            )}
            {/* Status pill stays inline with the title so it reads like
                "Lec 1.pdf - Processing" at a glance. Ready state shows
                no pill (default = good). */}
            {!isRenaming && apiStatus && !isSoftDeleted && (
              <StatusPill status={apiStatus} blocked={isBlocked} />
            )}
            {/* Deleted badge - surfaces soft-deleted sources on past-version
                views so the user can tell the file is gone but understand it
                was part of this version. Native title tooltip explains that
                re-uploading creates a new entry rather than restoring this
                one (past versions reference the original UUID). */}
            {!isRenaming && isSoftDeleted && (
              <span
                title='This file was deleted. Re-uploads create new entries.'
                className='inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground'
              >
                <Trash2 className='h-3 w-3' strokeWidth={2.5} />
                Deleted
              </span>
            )}
          </div>
          {/* Metadata line - type + size + time. Type pill removed; the
              colored file icon already conveys it. Single dot separator,
              kept quiet and uniform. */}
          <p className='mt-1 truncate text-xs text-muted-foreground'>
            {[getDisplayType(), size, getTimeAgo()].filter(Boolean).join(' · ')}
          </p>
          {/* The document was longer than the plan's per-source page cap and
              had no chapters to pick from, so only its first pages were read. */}
          {pageLimitApplied ? (
            <p className='mt-1 text-xs text-muted-foreground'>
              Only the first {pageLimitApplied} of {pageCount ?? 'its'} pages
              were used (your plan&apos;s page limit per document).
            </p>
          ) : null}
          {/* Content-policy explanation for a blocked source. Shown inline
              (not a tooltip) so the reason is unmissable and the user knows a
              retry won't help - editing/removing the source is the path.

              `text-warning-emphasis`, not `text-amber-700`: this is the ONLY
              copy explaining why there is no Retry, and amber-700 measured
              4.26:1 on the row surface at 12px, under the 4.5 text gate.
              Caught by `App/…/Sources/RetriesAFailedSource`. */}
          {isBlocked && (
            <p className='mt-1 text-xs text-warning-emphasis'>
              {sourceError?.message ||
                "This source was removed because it didn't pass our content policy."}
            </p>
          )}
        </div>
      </div>

      <div ref={menuRef} className='relative flex items-center gap-1'>
        {/* Inline retry for a failed source - re-ingests it and re-opens the
            context latch. Shown only when the source genuinely failed and the
            panel is editable (not a read-only past version). */}
        {isFailed &&
          !isBlocked &&
          !readOnly &&
          canRetry &&
          isApiSource(source) && (
            <Button
              variant='ghost'
              size='sm'
              // `text-destructive-emphasis`, not `text-rose-600`: rose-600 is
              // 4.46:1 on the card at 12px — just under AA, which is exactly the
              // kind of near-miss that survives review.
              className='tap-target h-6 gap-1 px-2 text-xs font-medium text-destructive-emphasis hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-900/20'
              onClick={handleRetrySource}
              disabled={retryMutation.isPending}
            >
              {retryMutation.isPending ? (
                <Loader2 className='h-3 w-3 animate-spin motion-reduce:animate-none' />
              ) : (
                <RefreshCw className='h-3 w-3' />
              )}
              Retry
            </Button>
          )}
        {!isProcessing && !isSoftDeleted && (
          <Button
            variant='ghost'
            size='sm'
            // Named per source, not "More options". The panel renders one of
            // these per row, and a screen reader's element list full of
            // identical "More options" buttons is unusable — the same reason
            // the Extras delete controls carry their attachment's title.
            //
            // It is also `opacity-0` until the row is hovered, which hides it
            // from sight but NOT from the tab order: a keyboard user has no
            // hover, so this is their only route to Rename / Download / Remove.
            // Nor does a finger: on a phone or any touch screen it is always
            // shown, with a 44px tap area, or the sources sheet had no
            // visible way to those actions at all.
            aria-label={`Source options for ${sourceName}`}
            aria-expanded={showMenu}
            className='tap-target h-6 w-6 p-0 opacity-0 transition-all duration-200 focus-visible:opacity-100 group-hover:opacity-100 max-sm:opacity-100 [@media(pointer:coarse)]:opacity-100'
            onClick={e => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
          >
            <MoreVertical className='h-3 w-3' aria-hidden='true' />
          </Button>
        )}

        {showMenu && (
          <>
            {/* Menu - dismissed by `useDismiss`, not a click-catcher. */}
            <div className='absolute right-0 top-7 z-20 w-40 rounded-lg border border-border bg-card py-1 shadow-lg max-sm:top-[34px] sm:w-32 [@media(pointer:coarse)]:top-[34px]'>
              <button
                className='flex w-full items-center gap-2 px-3 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2 sm:text-xs'
                onClick={handleView}
              >
                <Eye className='h-3 w-3' />
                View
              </button>
              {isApiSource(source) && !readOnly && !isSoftDeleted && (
                <button
                  className='flex w-full items-center gap-2 px-3 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2 sm:text-xs'
                  onClick={e => {
                    e.stopPropagation();
                    beginRename();
                  }}
                >
                  <Pencil className='h-3 w-3' />
                  Rename
                </button>
              )}
              {showDownload && !isSoftDeleted && (
                <button
                  className='flex w-full items-center gap-2 px-3 py-3 text-left text-sm text-foreground transition-colors hover:bg-muted sm:py-2 sm:text-xs'
                  onClick={() => setShowMenu(false)}
                >
                  <Download className='h-3 w-3' />
                  Download
                </button>
              )}
              {!readOnly && !isSoftDeleted && (
                <button
                  // `text-destructive-emphasis`, not `text-red-600` — 4.47:1
                  // at 12px on the menu surface. The repo has already made
                  // this exact swap on two other delete menu items; this is
                  // the third instance of the same near-miss.
                  className='flex w-full items-center gap-2 px-3 py-3 text-left text-sm text-destructive-emphasis transition-colors hover:bg-muted dark:text-red-400 sm:py-2 sm:text-xs'
                  onClick={e => {
                    e.stopPropagation();
                    onRemove();
                    setShowMenu(false);
                  }}
                >
                  <Trash2 className='h-3 w-3' />
                  Remove
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
